import type { CommercialAreaStore, CommercialCategoryCount, CommercialDensityLevel } from '../market/types';
import type { HousingComparisonAreaBasis, HousingMarketComparisonScope, HousingUnitType } from '../signals/types';

export type InboxKind = 'HOUSING' | 'BID' | 'SUPPORT' | 'SITE' | 'FRANCHISE' | 'COMMERCIAL';
export type InboxOriginMode = 'LIVE' | 'DEMO';
export type InboxReviewStatus = 'INTEREST' | 'REVIEWING' | 'PREPARING' | 'APPLIED' | 'HOLD';
export type DecisionAmountRole = 'CONTRACT_VALUE' | 'SUPPORT_AMOUNT';

export interface InboxItem {
  key: string;
  sourceId: string;
  kind: InboxKind;
  title: string;
  summary?: string;
  organization?: string;
  region?: string;
  target?: string;
  amount?: number;
  amountRole?: DecisionAmountRole;
  amountProvenance?: 'OFFICIAL' | 'DERIVED';
  amountDetail?: string;
  units?: number;
  areaSqm?: number;
  priceMin?: number;
  priceMax?: number;
  competitionRate?: number;
  nearbyMedianSalePrice?: number;
  nearbyMedianUnitPricePerSqm?: number;
  marketReferenceEstimatedSalePrice?: number;
  marketReferenceHouseType?: string;
  marketReferenceAreaSqm?: number;
  marketReferenceAreaBasis?: HousingComparisonAreaBasis;
  marketSampleCount?: number;
  marketComparisonScope?: HousingMarketComparisonScope;
  marketComparisonDetail?: string;
  housingUnitTypes?: readonly HousingUnitType[];
  applicationMethod?: string;
  selfContributionRate?: number;
  executingOrganization?: string;
  contact?: string;
  applicationUrl?: string;
  attachmentUrl?: string;
  attachmentName?: string;
  basePrice?: number;
  qualificationSummary?: string;
  industryCodes?: readonly string[];
  historicalAwardRate?: number;
  historicalContractCount?: number;
  radiusMeters?: number;
  totalStoreCount?: number;
  sampledStoreCount?: number;
  sameIndustrySampleCount?: number;
  competitionScore?: number;
  sampleCoverage?: number;
  densityLevel?: CommercialDensityLevel;
  sameIndustryStores?: readonly CommercialAreaStore[];
  categoryBreakdown?: readonly CommercialCategoryCount[];
  publishedAt?: string;
  startsAt?: string;
  closesAt?: string;
  winnerAt?: string;
  sourceUrl?: string;
  sourceLabel: string;
  sourceRoute: string;
  originMode: InboxOriginMode;
  reviewStatus?: InboxReviewStatus;
  savedAt: string;
  resolvedAt: string;
}

export interface DecisionContext {
  kind: InboxKind;
  title: string;
  sourceLabel: string;
  sourceRoute: string;
  originMode?: InboxOriginMode;
  organization?: string;
  region?: string;
  target?: string;
  amount?: number;
  amountRole?: DecisionAmountRole;
  amountProvenance?: 'OFFICIAL' | 'DERIVED';
  amountDetail?: string;
  units?: number;
  areaSqm?: number;
  priceMin?: number;
  priceMax?: number;
  competitionRate?: number;
  nearbyMedianSalePrice?: number;
  nearbyMedianUnitPricePerSqm?: number;
  marketReferenceEstimatedSalePrice?: number;
  marketReferenceHouseType?: string;
  marketReferenceAreaSqm?: number;
  marketReferenceAreaBasis?: HousingComparisonAreaBasis;
  marketSampleCount?: number;
  marketComparisonScope?: HousingMarketComparisonScope;
  marketComparisonDetail?: string;
  housingUnitTypes?: readonly HousingUnitType[];
  applicationMethod?: string;
  selfContributionRate?: number;
  executingOrganization?: string;
  contact?: string;
  applicationUrl?: string;
  attachmentUrl?: string;
  attachmentName?: string;
  basePrice?: number;
  qualificationSummary?: string;
  industryCodes?: readonly string[];
  historicalAwardRate?: number;
  historicalContractCount?: number;
  radiusMeters?: number;
  totalStoreCount?: number;
  sampledStoreCount?: number;
  sameIndustrySampleCount?: number;
  competitionScore?: number;
  sampleCoverage?: number;
  densityLevel?: CommercialDensityLevel;
  sameIndustryStores?: readonly CommercialAreaStore[];
  categoryBreakdown?: readonly CommercialCategoryCount[];
  closesAt?: string;
  sourceUrl?: string;
  savedAt: string;
}

const INBOX_KEY = 'business-calculator-kr:inbox:v1';
const DECISION_CONTEXT_KEY = 'business-calculator-kr:decision-context:v1';
const MAX_INBOX_ITEMS = 300;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function numberValue(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined;
}

function kind(value: unknown): InboxKind | undefined {
  return value === 'HOUSING' || value === 'BID' || value === 'SUPPORT' || value === 'SITE' || value === 'FRANCHISE' || value === 'COMMERCIAL' ? value : undefined;
}

function amountRole(value: unknown): DecisionAmountRole | undefined {
  return value === 'CONTRACT_VALUE' || value === 'SUPPORT_AMOUNT' ? value : undefined;
}

function stringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const values = value.map(text).filter((item): item is string => Boolean(item));
  return values.length > 0 ? values : undefined;
}

function housingUnitTypes(value: unknown): HousingUnitType[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const rows = value.flatMap((entry): HousingUnitType[] => {
    if (!isRecord(entry)) return [];
    const houseType = text(entry.houseType);
    if (!houseType) return [];
    return [{
      ...(text(entry.modelNo) ? { modelNo: text(entry.modelNo) } : {}),
      houseType,
      ...(numberValue(entry.supplyAreaSqm) !== undefined ? { supplyAreaSqm: numberValue(entry.supplyAreaSqm) } : {}),
      ...(numberValue(entry.comparisonAreaSqm) !== undefined ? { comparisonAreaSqm: numberValue(entry.comparisonAreaSqm) } : {}),
      ...(entry.comparisonAreaBasis === 'OFFICIAL_EXCLUSIVE' || entry.comparisonAreaBasis === 'HOUSE_TYPE_PROXY' ? { comparisonAreaBasis: entry.comparisonAreaBasis } : {}),
      ...(numberValue(entry.generalSupplyUnits) !== undefined ? { generalSupplyUnits: numberValue(entry.generalSupplyUnits) } : {}),
      ...(numberValue(entry.specialSupplyUnits) !== undefined ? { specialSupplyUnits: numberValue(entry.specialSupplyUnits) } : {}),
      ...(numberValue(entry.totalSupplyUnits) !== undefined ? { totalSupplyUnits: numberValue(entry.totalSupplyUnits) } : {}),
      ...(numberValue(entry.priceMax) !== undefined ? { priceMax: numberValue(entry.priceMax) } : {}),
      ...(numberValue(entry.competitionRate) !== undefined ? { competitionRate: numberValue(entry.competitionRate) } : {}),
      ...(numberValue(entry.applicants) !== undefined ? { applicants: numberValue(entry.applicants) } : {}),
    }];
  });
  return rows.length > 0 ? rows : undefined;
}

function originMode(value: unknown): InboxOriginMode | undefined {
  return value === 'LIVE' || value === 'DEMO' ? value : undefined;
}
function housingComparisonAreaBasis(value: unknown): HousingComparisonAreaBasis | undefined {
  return value === 'OFFICIAL_EXCLUSIVE' || value === 'HOUSE_TYPE_PROXY' ? value : undefined;
}

function housingMarketComparisonScope(value: unknown): HousingMarketComparisonScope | undefined {
  return value === 'SAME_UMD_SIMILAR_AREA' || value === 'DISTRICT_SIMILAR_AREA'
    || value === 'SAME_UMD_ALL_AREA' || value === 'DISTRICT_ALL_AREA' ? value : undefined;
}

function densityLevel(value: unknown): CommercialDensityLevel | undefined {
  return value === 'LOW' || value === 'MEDIUM' || value === 'HIGH' ? value : undefined;
}

function commercialStores(value: unknown): CommercialAreaStore[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const rows = value.flatMap((entry): CommercialAreaStore[] => {
    if (!isRecord(entry)) return [];
    const name = text(entry.name);
    const category = text(entry.category);
    if (!name || !category) return [];
    return [{
      name,
      ...(text(entry.branchName) ? { branchName: text(entry.branchName) } : {}),
      category,
      ...(text(entry.address) ? { address: text(entry.address) } : {}),
      ...(numberValue(entry.distanceMeters) !== undefined ? { distanceMeters: numberValue(entry.distanceMeters) } : {}),
    }];
  });
  return rows.length > 0 ? rows : undefined;
}

function commercialCategoryBreakdown(value: unknown): CommercialCategoryCount[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const rows = value.flatMap((entry): CommercialCategoryCount[] => {
    if (!isRecord(entry)) return [];
    const category = text(entry.category);
    const count = numberValue(entry.count);
    return category && count !== undefined ? [{ category, count }] : [];
  });
  return rows.length > 0 ? rows : undefined;
}


function reviewStatus(value: unknown): InboxReviewStatus {
  return value === 'REVIEWING' || value === 'PREPARING' || value === 'APPLIED' || value === 'HOLD' || value === 'INTEREST' ? value : 'INTEREST';
}

function normalizeItem(value: unknown): InboxItem | null {
  if (!isRecord(value)) return null;
  const parsedKind = kind(value.kind);
  const parsedMode = originMode(value.originMode);
  const key = text(value.key);
  const sourceId = text(value.sourceId);
  const title = text(value.title);
  const sourceLabel = text(value.sourceLabel);
  const sourceRoute = text(value.sourceRoute);
  const savedAt = text(value.savedAt);
  const resolvedAt = text(value.resolvedAt);
  if (!parsedKind || !parsedMode || !key || !sourceId || !title || !sourceLabel || !sourceRoute || !savedAt || !resolvedAt) return null;

  return {
    key, sourceId, kind: parsedKind, title,
    ...(text(value.summary) ? { summary: text(value.summary) } : {}),
    ...(text(value.organization) ? { organization: text(value.organization) } : {}),
    ...(text(value.region) ? { region: text(value.region) } : {}),
    ...(text(value.target) ? { target: text(value.target) } : {}),
    ...(numberValue(value.amount) !== undefined ? { amount: numberValue(value.amount) } : {}),
    ...(amountRole(value.amountRole) ? { amountRole: amountRole(value.amountRole) } : {}),
    ...(value.amountProvenance === 'OFFICIAL' || value.amountProvenance === 'DERIVED' ? { amountProvenance: value.amountProvenance } : {}),
    ...(text(value.amountDetail) ? { amountDetail: text(value.amountDetail) } : {}),
    ...(numberValue(value.units) !== undefined ? { units: numberValue(value.units) } : {}),
    ...(numberValue(value.areaSqm) !== undefined ? { areaSqm: numberValue(value.areaSqm) } : {}),
    ...(numberValue(value.priceMin) !== undefined ? { priceMin: numberValue(value.priceMin) } : {}),
    ...(numberValue(value.priceMax) !== undefined ? { priceMax: numberValue(value.priceMax) } : {}),
    ...(numberValue(value.competitionRate) !== undefined ? { competitionRate: numberValue(value.competitionRate) } : {}),
    ...(numberValue(value.nearbyMedianSalePrice) !== undefined ? { nearbyMedianSalePrice: numberValue(value.nearbyMedianSalePrice) } : {}),
    ...(numberValue(value.nearbyMedianUnitPricePerSqm) !== undefined ? { nearbyMedianUnitPricePerSqm: numberValue(value.nearbyMedianUnitPricePerSqm) } : {}),
    ...(numberValue(value.marketReferenceEstimatedSalePrice) !== undefined ? { marketReferenceEstimatedSalePrice: numberValue(value.marketReferenceEstimatedSalePrice) } : {}),
    ...(text(value.marketReferenceHouseType) ? { marketReferenceHouseType: text(value.marketReferenceHouseType) } : {}),
    ...(numberValue(value.marketReferenceAreaSqm) !== undefined ? { marketReferenceAreaSqm: numberValue(value.marketReferenceAreaSqm) } : {}),
    ...(housingComparisonAreaBasis(value.marketReferenceAreaBasis) ? { marketReferenceAreaBasis: housingComparisonAreaBasis(value.marketReferenceAreaBasis) } : {}),
    ...(numberValue(value.marketSampleCount) !== undefined ? { marketSampleCount: numberValue(value.marketSampleCount) } : {}),
    ...(housingMarketComparisonScope(value.marketComparisonScope) ? { marketComparisonScope: housingMarketComparisonScope(value.marketComparisonScope) } : {}),
    ...(text(value.marketComparisonDetail) ? { marketComparisonDetail: text(value.marketComparisonDetail) } : {}),
    ...(housingUnitTypes(value.housingUnitTypes) ? { housingUnitTypes: housingUnitTypes(value.housingUnitTypes) } : {}),
    ...(text(value.applicationMethod) ? { applicationMethod: text(value.applicationMethod) } : {}),
    ...(numberValue(value.selfContributionRate) !== undefined ? { selfContributionRate: numberValue(value.selfContributionRate) } : {}),
    ...(text(value.executingOrganization) ? { executingOrganization: text(value.executingOrganization) } : {}),
    ...(text(value.contact) ? { contact: text(value.contact) } : {}),
    ...(text(value.applicationUrl) ? { applicationUrl: text(value.applicationUrl) } : {}),
    ...(text(value.attachmentUrl) ? { attachmentUrl: text(value.attachmentUrl) } : {}),
    ...(text(value.attachmentName) ? { attachmentName: text(value.attachmentName) } : {}),
    ...(numberValue(value.basePrice) !== undefined ? { basePrice: numberValue(value.basePrice) } : {}),
    ...(text(value.qualificationSummary) ? { qualificationSummary: text(value.qualificationSummary) } : {}),
    ...(stringArray(value.industryCodes) ? { industryCodes: stringArray(value.industryCodes) } : {}),
    ...(numberValue(value.historicalAwardRate) !== undefined ? { historicalAwardRate: numberValue(value.historicalAwardRate) } : {}),
    ...(numberValue(value.historicalContractCount) !== undefined ? { historicalContractCount: numberValue(value.historicalContractCount) } : {}),
    ...(numberValue(value.radiusMeters) !== undefined ? { radiusMeters: numberValue(value.radiusMeters) } : {}),
    ...(numberValue(value.totalStoreCount) !== undefined ? { totalStoreCount: numberValue(value.totalStoreCount) } : {}),
    ...(numberValue(value.sampledStoreCount) !== undefined ? { sampledStoreCount: numberValue(value.sampledStoreCount) } : {}),
    ...(numberValue(value.sameIndustrySampleCount) !== undefined ? { sameIndustrySampleCount: numberValue(value.sameIndustrySampleCount) } : {}),
    ...(numberValue(value.competitionScore) !== undefined ? { competitionScore: numberValue(value.competitionScore) } : {}),
    ...(numberValue(value.sampleCoverage) !== undefined ? { sampleCoverage: numberValue(value.sampleCoverage) } : {}),
    ...(densityLevel(value.densityLevel) ? { densityLevel: densityLevel(value.densityLevel) } : {}),
    ...(commercialStores(value.sameIndustryStores) ? { sameIndustryStores: commercialStores(value.sameIndustryStores) } : {}),
    ...(commercialCategoryBreakdown(value.categoryBreakdown) ? { categoryBreakdown: commercialCategoryBreakdown(value.categoryBreakdown) } : {}),
    ...(text(value.publishedAt) ? { publishedAt: text(value.publishedAt) } : {}),
    ...(text(value.startsAt) ? { startsAt: text(value.startsAt) } : {}),
    ...(text(value.closesAt) ? { closesAt: text(value.closesAt) } : {}),
    ...(text(value.winnerAt) ? { winnerAt: text(value.winnerAt) } : {}),
    ...(text(value.sourceUrl) ? { sourceUrl: text(value.sourceUrl) } : {}),
    sourceLabel, sourceRoute, originMode: parsedMode, reviewStatus: reviewStatus(value.reviewStatus), savedAt, resolvedAt,
  };
}

export function inboxKey(kindValue: InboxKind, sourceId: string): string {
  return `${kindValue}:${sourceId}`;
}

export function loadInboxItems(): InboxItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(INBOX_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeItem).filter((item): item is InboxItem => item !== null).slice(0, MAX_INBOX_ITEMS);
  } catch { return []; }
}

export function saveInboxItems(items: readonly InboxItem[]): void {
  if (typeof window === 'undefined') return;
  try {
    const normalized = items.map(normalizeItem).filter((item): item is InboxItem => item !== null).slice(0, MAX_INBOX_ITEMS);
    window.localStorage.setItem(INBOX_KEY, JSON.stringify(normalized));
  } catch { /* local persistence must not block review */ }
}

export function mergeInboxItems(existing: readonly InboxItem[], incoming: readonly InboxItem[]): InboxItem[] {
  const merged = new Map<string, InboxItem>();
  for (const item of existing) merged.set(item.key, item);
  for (const item of incoming) {
    const previous = merged.get(item.key);
    merged.set(item.key, previous ? { ...item, savedAt: previous.savedAt, reviewStatus: previous.reviewStatus ?? 'INTEREST' } : { ...item, reviewStatus: item.reviewStatus ?? 'INTEREST' });
  }
  return [...merged.values()].sort((a, b) => b.savedAt.localeCompare(a.savedAt)).slice(0, MAX_INBOX_ITEMS);
}

export function removeInboxItem(key: string): InboxItem[] {
  const next = loadInboxItems().filter((item) => item.key !== key);
  saveInboxItems(next);
  return next;
}

export function updateInboxReviewStatus(key: string, status: InboxReviewStatus): InboxItem[] {
  const next = loadInboxItems().map((item) => item.key === key ? { ...item, reviewStatus: status } : item);
  saveInboxItems(next);
  return next;
}

export function saveDecisionContext(context: DecisionContext): void {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.setItem(DECISION_CONTEXT_KEY, JSON.stringify(context)); } catch { /* optional */ }
}

export function loadDecisionContext(): DecisionContext | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(DECISION_CONTEXT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    const parsedKind = kind(parsed.kind);
    const title = text(parsed.title);
    const sourceLabel = text(parsed.sourceLabel);
    const sourceRoute = text(parsed.sourceRoute);
    const savedAt = text(parsed.savedAt);
    if (!parsedKind || !title || !sourceLabel || !sourceRoute || !savedAt) return null;
    return {
      kind: parsedKind,
      title,
      sourceLabel,
      sourceRoute,
      ...(originMode(parsed.originMode) ? { originMode: originMode(parsed.originMode) } : {}),
      ...(text(parsed.organization) ? { organization: text(parsed.organization) } : {}),
      ...(text(parsed.region) ? { region: text(parsed.region) } : {}),
      ...(text(parsed.target) ? { target: text(parsed.target) } : {}),
      ...(numberValue(parsed.amount) !== undefined ? { amount: numberValue(parsed.amount) } : {}),
      ...(amountRole(parsed.amountRole) ? { amountRole: amountRole(parsed.amountRole) } : {}),
      ...(parsed.amountProvenance === 'OFFICIAL' || parsed.amountProvenance === 'DERIVED' ? { amountProvenance: parsed.amountProvenance } : {}),
      ...(text(parsed.amountDetail) ? { amountDetail: text(parsed.amountDetail) } : {}),
      ...(numberValue(parsed.units) !== undefined ? { units: numberValue(parsed.units) } : {}),
      ...(numberValue(parsed.areaSqm) !== undefined ? { areaSqm: numberValue(parsed.areaSqm) } : {}),
      ...(numberValue(parsed.priceMin) !== undefined ? { priceMin: numberValue(parsed.priceMin) } : {}),
      ...(numberValue(parsed.priceMax) !== undefined ? { priceMax: numberValue(parsed.priceMax) } : {}),
      ...(numberValue(parsed.competitionRate) !== undefined ? { competitionRate: numberValue(parsed.competitionRate) } : {}),
      ...(numberValue(parsed.nearbyMedianSalePrice) !== undefined ? { nearbyMedianSalePrice: numberValue(parsed.nearbyMedianSalePrice) } : {}),
      ...(numberValue(parsed.nearbyMedianUnitPricePerSqm) !== undefined ? { nearbyMedianUnitPricePerSqm: numberValue(parsed.nearbyMedianUnitPricePerSqm) } : {}),
      ...(numberValue(parsed.marketReferenceEstimatedSalePrice) !== undefined ? { marketReferenceEstimatedSalePrice: numberValue(parsed.marketReferenceEstimatedSalePrice) } : {}),
      ...(text(parsed.marketReferenceHouseType) ? { marketReferenceHouseType: text(parsed.marketReferenceHouseType) } : {}),
      ...(numberValue(parsed.marketReferenceAreaSqm) !== undefined ? { marketReferenceAreaSqm: numberValue(parsed.marketReferenceAreaSqm) } : {}),
      ...(housingComparisonAreaBasis(parsed.marketReferenceAreaBasis) ? { marketReferenceAreaBasis: housingComparisonAreaBasis(parsed.marketReferenceAreaBasis) } : {}),
      ...(numberValue(parsed.marketSampleCount) !== undefined ? { marketSampleCount: numberValue(parsed.marketSampleCount) } : {}),
      ...(housingMarketComparisonScope(parsed.marketComparisonScope) ? { marketComparisonScope: housingMarketComparisonScope(parsed.marketComparisonScope) } : {}),
      ...(text(parsed.marketComparisonDetail) ? { marketComparisonDetail: text(parsed.marketComparisonDetail) } : {}),
      ...(housingUnitTypes(parsed.housingUnitTypes) ? { housingUnitTypes: housingUnitTypes(parsed.housingUnitTypes) } : {}),
      ...(text(parsed.applicationMethod) ? { applicationMethod: text(parsed.applicationMethod) } : {}),
      ...(numberValue(parsed.selfContributionRate) !== undefined ? { selfContributionRate: numberValue(parsed.selfContributionRate) } : {}),
      ...(text(parsed.executingOrganization) ? { executingOrganization: text(parsed.executingOrganization) } : {}),
      ...(text(parsed.contact) ? { contact: text(parsed.contact) } : {}),
      ...(text(parsed.applicationUrl) ? { applicationUrl: text(parsed.applicationUrl) } : {}),
      ...(text(parsed.attachmentUrl) ? { attachmentUrl: text(parsed.attachmentUrl) } : {}),
      ...(text(parsed.attachmentName) ? { attachmentName: text(parsed.attachmentName) } : {}),
      ...(numberValue(parsed.basePrice) !== undefined ? { basePrice: numberValue(parsed.basePrice) } : {}),
      ...(text(parsed.qualificationSummary) ? { qualificationSummary: text(parsed.qualificationSummary) } : {}),
      ...(stringArray(parsed.industryCodes) ? { industryCodes: stringArray(parsed.industryCodes) } : {}),
      ...(numberValue(parsed.historicalAwardRate) !== undefined ? { historicalAwardRate: numberValue(parsed.historicalAwardRate) } : {}),
      ...(numberValue(parsed.historicalContractCount) !== undefined ? { historicalContractCount: numberValue(parsed.historicalContractCount) } : {}),
      ...(numberValue(parsed.radiusMeters) !== undefined ? { radiusMeters: numberValue(parsed.radiusMeters) } : {}),
      ...(numberValue(parsed.totalStoreCount) !== undefined ? { totalStoreCount: numberValue(parsed.totalStoreCount) } : {}),
      ...(numberValue(parsed.sampledStoreCount) !== undefined ? { sampledStoreCount: numberValue(parsed.sampledStoreCount) } : {}),
      ...(numberValue(parsed.sameIndustrySampleCount) !== undefined ? { sameIndustrySampleCount: numberValue(parsed.sameIndustrySampleCount) } : {}),
      ...(numberValue(parsed.competitionScore) !== undefined ? { competitionScore: numberValue(parsed.competitionScore) } : {}),
      ...(numberValue(parsed.sampleCoverage) !== undefined ? { sampleCoverage: numberValue(parsed.sampleCoverage) } : {}),
      ...(densityLevel(parsed.densityLevel) ? { densityLevel: densityLevel(parsed.densityLevel) } : {}),
      ...(commercialStores(parsed.sameIndustryStores) ? { sameIndustryStores: commercialStores(parsed.sameIndustryStores) } : {}),
      ...(commercialCategoryBreakdown(parsed.categoryBreakdown) ? { categoryBreakdown: commercialCategoryBreakdown(parsed.categoryBreakdown) } : {}),
      ...(text(parsed.closesAt) ? { closesAt: text(parsed.closesAt) } : {}),
      ...(text(parsed.sourceUrl) ? { sourceUrl: text(parsed.sourceUrl) } : {}),
      savedAt,
    };
  } catch { return null; }
}

export function clearDecisionContext(): void {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.removeItem(DECISION_CONTEXT_KEY); } catch { /* ignore */ }
}
